import * as THREE from 'three';
import { Random } from '../core/Random';
import type { CharacterLook } from '../state/types';
import { batchStatic } from './batch';
import { rideDef, type RideId } from '../content/rides';
import { buildRide } from './builders/rides';
import { buildTruckNutz, type BuiltCar } from './builders/vehicles';
import { cooler, couch, kettleGrill, lawnChair, mailbox, trashBags } from './builders/props';
import { buildHomeDoubleWide } from './builders/doublewide';
import { buildTrailer, type TrailerSpec } from './builders/trailer';
import type { CollisionWorld } from './Collision';
import { frameRot, HOME_SIZE, homeSpots, type HomeFrame, type HomeKind, type HomeSpots } from './home';

/** What a home needs from the world it sits in. */
export interface HomeWorld {
  readonly collision: CollisionWorld;
  readonly occluders: THREE.Box3[];
}

export interface TrailerStyle {
  color: string;
  stripe: string;
}

/** Trailer paint follows your shirt: the default white tank gets the classic mint. */
const STYLES: Record<string, TrailerStyle> = {
  '#f3efe4': { color: '#cfe6d2', stripe: '#8b5a2b' },
  '#d6452f': { color: '#f2e3c6', stripe: '#b5402a' },
  '#2b6cb0': { color: '#d7e3f2', stripe: '#2b4f8a' },
  '#ffd400': { color: '#f4ecc4', stripe: '#c79a12' },
  '#2f8f3a': { color: '#dce9cf', stripe: '#3f6b2a' },
  '#1d1d1d': { color: '#dcd6cf', stripe: '#3a3a3a' },
  '#ff6fa8': { color: '#f3d9e6', stripe: '#a8346b' },
  '#7a1f2b': { color: '#efe2cf', stripe: '#7a1f2b' },
};

export function trailerStyle(look: CharacterLook): TrailerStyle {
  const known = STYLES[look.topColor.toLowerCase()];
  if (known) return known;
  const all = Object.values(STYLES);
  let hash = 0;
  for (const ch of look.topColor) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return all[Math.abs(hash) % all.length] as TrailerStyle;
}

function trailerSpec(style: TrailerStyle, side: number): TrailerSpec {
  // Lot 7's layout; homes across the street mirror it end for end.
  const flip = -side;
  return {
    color: style.color,
    stripe: style.stripe,
    windows: [
      { x: -3.4 * flip, side: 1, kind: 'glass' },
      { x: 0.8 * flip, side: 1, kind: 'flag' },
      { x: -4.4 * flip, side: -1, kind: 'foil' },
      { x: 1.4 * flip, side: -1, kind: 'dim' },
    ],
    ac: -1.3 * flip,
    skirting: 'lattice',
    steps: 'blocks',
    awning: true,
    porchLight: true,
    seed: 7,
    doorX: -side * 4,
    hitch: -side,
  };
}

export type ParkedCar = 'blocks' | 'fixed' | 'none';

/**
 * Everything on a player's lot except the yard decor: the trailer, porch couch,
 * cooler, grill, mailbox and lawn chair. Built at any HomeFrame, mirrored as needed,
 * and merged into a handful of meshes. Other players' homes also park their Camaro.
 */
export class HomeLot {
  readonly group = new THREE.Group();
  readonly spots: HomeSpots;
  private readonly colliders: number[] = [];
  private readonly boxes: THREE.Box3[] = [];
  private readonly carRoot = new THREE.Group();
  private car: BuiltCar | null = null;
  private carColliders: number[] = [];
  private parked: ParkedCar = 'none';
  private parkedRide: RideId = 'camaro';
  private truckNutz = false;

  constructor(
    private readonly world: HomeWorld,
    parent: THREE.Object3D,
    readonly frame: HomeFrame,
    style: TrailerStyle,
    label: string,
    readonly kind: HomeKind = 'single',
  ) {
    const h = homeSpots(frame, kind);
    this.spots = h;
    const side = frame.side;
    const rng = new Random(1000 + frame.index);
    const add = (obj: THREE.Object3D, x: number, z: number, rot = 0): void => {
      obj.position.set(x, obj.position.y, z);
      obj.rotation.y = rot;
      this.group.add(obj);
    };

    const size = HOME_SIZE[kind];
    if (kind === 'double') {
      add(buildHomeDoubleWide({ ...style, flip: side === -1 ? 1 : -1 }), h.trailer.x, h.trailer.z);
      this.solid(h.trailer.x, h.trailer.z, size.length, size.depth, 4.4);
      // The porch deck and its steps.
      this.colliders.push(
        world.collision.addRect(h.trailerDoor.x, h.trailerDoor.z + 0.95, 3.5, 1.95),
        world.collision.addRect(h.trailerDoor.x, h.trailerDoor.z + 2.1, 1.0, 0.45),
      );
    } else {
      add(buildTrailer(trailerSpec(style, side)), h.trailer.x, h.trailer.z);
      this.solid(h.trailer.x, h.trailer.z, size.length, size.depth, 3.4);
      this.colliders.push(
        world.collision.addRect(h.trailerDoor.x, h.trailerDoor.z + 0.45, 1.0, 0.9),
        // Hitch tongue at the street end.
        world.collision.addRect(h.trailer.x - side * 7.2, h.trailer.z, 2.2, 1.2),
      );
    }

    add(couch('#b5651d'), h.couch.x, h.couch.z - 0.1);
    this.colliders.push(world.collision.addRect(h.couch.x, h.couch.z - 0.1, 2.1, 0.9));
    add(cooler(), h.couch.x + side * 1.5, h.couch.z - 0.15);
    add(kettleGrill(), h.grill.x, h.grill.z - 0.3);
    this.colliders.push(world.collision.addCircle(h.grill.x, h.grill.z - 0.3, 0.4));
    add(mailbox(label), h.mailbox.x, h.mailbox.z - 0.3, frameRot(frame, 0, 'x'));
    this.colliders.push(world.collision.addCircle(h.mailbox.x, h.mailbox.z - 0.3, 0.2));
    add(lawnChair('#2f8f3a'), h.chair.x, h.chair.z, h.chair.rot);
    add(trashBags(rng, 2), h.trash.x, h.trash.z);

    batchStatic(this.group);
    this.carRoot.position.set(h.camaro.x, 0, h.camaro.z);
    this.carRoot.rotation.y = h.camaro.heading;
    this.group.add(this.carRoot);
    parent.add(this.group);
  }

  /** Where the porch light hangs (world space). */
  porchLight(out: THREE.Vector3): THREE.Vector3 {
    const d = this.spots.trailerDoor;
    return out.set(d.x + this.frame.side * 0.7, this.kind === 'double' ? 2.3 : 2.4, d.z + 0.6);
  }

  /** Other players' homes show their ride in the driveway (unless they're out driving it). */
  setParkedCar(state: ParkedCar, ride: RideId = 'camaro'): void {
    if (state === this.parked && ride === this.parkedRide) return;
    this.parked = state;
    this.parkedRide = ride;
    this.carRoot.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.carRoot.clear();
    for (const id of this.carColliders) this.world.collision.remove(id);
    this.carColliders = [];
    this.car = null;
    if (state === 'none') return;
    this.car = buildRide(ride, { onBlocks: state === 'blocks' });
    this.carRoot.add(this.car.group);
    if (this.truckNutz) this.car.rear.add(buildTruckNutz());
    const c = this.spots.camaro;
    const fx = Math.cos(c.heading);
    const { axle, radius } = rideDef(ride).stats;
    for (const off of [axle, -axle])
      this.carColliders.push(this.world.collision.addCircle(c.x + fx * off, c.z, radius * 0.92));
  }

  /** Decor hook for Truck Nutz on a neighbor's parked Camaro. */
  setTruckNutz(on: boolean): void {
    if (on === this.truckNutz) return;
    this.truckNutz = on;
    if (on && this.car) this.car.rear.add(buildTruckNutz());
    else if (!on && this.parked !== 'none') {
      const state = this.parked;
      this.parked = 'none';
      this.setParkedCar(state, this.parkedRide);
    }
  }

  dispose(): void {
    this.setParkedCar('none');
    this.group.removeFromParent();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    for (const id of this.colliders) this.world.collision.remove(id);
    for (const b of this.boxes) {
      const i = this.world.occluders.indexOf(b);
      if (i >= 0) this.world.occluders.splice(i, 1);
    }
  }

  private solid(cx: number, cz: number, w: number, d: number, height: number): void {
    this.colliders.push(this.world.collision.addRect(cx, cz, w, d));
    const box = new THREE.Box3(
      new THREE.Vector3(cx - w / 2, 0, cz - d / 2),
      new THREE.Vector3(cx + w / 2, height, cz + d / 2),
    );
    this.world.occluders.push(box);
    this.boxes.push(box);
  }
}
